/**
 * panelStack — global Zustand store that tracks all open SidePanel instances.
 *
 * State shape
 * -----------
 * stack: Array<{ id: string, stackable: boolean }>   — panels in open order (last = top)
 *
 * Actions
 * -------
 * open(id, { stackable })   Push a panel onto the stack.
 *   - If stackable=false, all existing non-stackable panels are removed first.
 *   - If id is already in the stack, it is moved to the top.
 * close(id)                 Remove a panel by id.
 * closeAll()                Empty the stack.
 *
 * Computed
 * --------
 * topPanelId()              Returns the id of the last item in stack, or null.
 *
 * Global Esc listener (registered once at module evaluation time):
 *   Pressing Escape closes the topmost panel.
 */

import { create } from 'zustand';

const usePanelStack = create((set, get) => ({
  stack: [],

  open: (id, { stackable = false } = {}) => {
    set((state) => {
      // Remove the id if already present (will re-push to top)
      let next = state.stack.filter((item) => item.id !== id);

      // If new panel is not stackable, remove all existing non-stackable panels
      if (!stackable) {
        next = next.filter((item) => item.stackable);
      }

      next = [...next, { id, stackable }];
      return { stack: next };
    });
  },

  close: (id) => {
    set((state) => ({
      stack: state.stack.filter((item) => item.id !== id),
    }));
  },

  closeAll: () => {
    set({ stack: [] });
  },

  topPanelId: () => {
    const { stack } = get();
    if (stack.length === 0) return null;
    return stack[stack.length - 1].id;
  },
}));

// Register global Escape key listener exactly once.
// Closes the topmost panel when Escape is pressed.
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const topId = usePanelStack.getState().topPanelId();
    if (topId !== null) {
      usePanelStack.getState().close(topId);
    }
  });
}

export { usePanelStack };
export default usePanelStack;
